import { Link } from 'react-router-dom';
import { ShieldCheck, Lock, Eye, Trash, EnvelopeSimple, ArrowLeft } from '@phosphor-icons/react';

export default function Privacy() {
  const lastUpdated = 'April 11, 2026';

  return (
    <div className="min-h-screen bg-[#fafafa]">
      {/* Header */}
      <div className="bg-white border-b border-[#ebeef2]">
        <div className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 text-[#2d3338] hover:text-[#005bc1] transition-colors">
            <ArrowLeft size={20} weight="bold" />
            <div>
              <span className="font-bold text-xl tracking-tight">LifeAdmin</span>
              <span className="text-xs text-[#596065] ml-2">AI Chief of Staff</span>
            </div>
          </Link>
          <div className="flex items-center gap-2 text-[#0f6b3a]">
            <ShieldCheck size={20} weight="fill" />
            <span className="text-sm font-semibold">Privacy First</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-10">
          <h1 className="text-4xl font-bold text-[#2d3338] mb-3">Privacy Policy</h1>
          <p className="text-[#596065]">Last updated: {lastUpdated}</p>
        </div>

        <div className="space-y-10">
          {/* Overview */}
          <section className="bg-white rounded-2xl p-8 border border-[#ebeef2]">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#d6e3ff] flex items-center justify-center">
                <ShieldCheck size={22} className="text-[#005bc1]" weight="fill" />
              </div>
              <h2 className="text-xl font-bold text-[#2d3338]">Overview</h2>
            </div>
            <p className="text-[#596065] leading-relaxed">
              LifeAdmin AI ("we", "our", "us") is a personal life organizer that helps you manage bills, appointments, reminders, and subscriptions. 
              We are committed to protecting your privacy and being transparent about how we use your data. This policy explains what information 
              we collect, how we use it, and your rights regarding your data.
            </p>
          </section>

          {/* Data We Collect */}
          <section className="bg-white rounded-2xl p-8 border border-[#ebeef2]">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#ede9fe] flex items-center justify-center">
                <Eye size={22} className="text-[#5b21b6]" weight="fill" />
              </div>
              <h2 className="text-xl font-bold text-[#2d3338]">Information We Collect</h2>
            </div>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-[#2d3338] mb-1">Account Information</h3>
                <p className="text-sm text-[#596065] leading-relaxed">
                  When you create an account, we collect your name, email address, and password (stored as a secure hash). 
                  If you sign in with Google, we receive your name, email, and profile picture from Google.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-[#2d3338] mb-1">Gmail Data</h3>
                <p className="text-sm text-[#596065] leading-relaxed">
                  When you connect your Gmail account, we access your emails in read-only mode to identify bills, appointments, 
                  subscriptions, and actionable tasks. We only read email metadata (subject, sender, snippet) and do not store 
                  full email content. We use Gmail API's restricted scopes and comply with Google's API Services User Data Policy.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-[#2d3338] mb-1">Google Calendar Data</h3>
                <p className="text-sm text-[#596065] leading-relaxed">
                  When you enable Google Calendar sync, we read your calendar events and can create events for your tasks and bills. 
                  We only access your primary calendar and do not modify or delete existing events.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-[#2d3338] mb-1">Task Data</h3>
                <p className="text-sm text-[#596065] leading-relaxed">
                  Tasks, bills, appointments, and related information (amounts, due dates, providers) that are either extracted 
                  from your email or manually created by you.
                </p>
              </div>
            </div>
          </section>

          {/* How We Use Data */}
          <section className="bg-white rounded-2xl p-8 border border-[#ebeef2]">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#dcfce7] flex items-center justify-center">
                <Lock size={22} className="text-[#0f6b3a]" weight="fill" />
              </div>
              <h2 className="text-xl font-bold text-[#2d3338]">How We Use Your Data</h2>
            </div>
            <ul className="space-y-3">
              {[
                'Extract and organize bills, appointments, and subscriptions from your email',
                'Provide AI-powered insights and recommendations to help you save money',
                'Send you reminders about upcoming due dates and deadlines',
                'Sync your tasks to Google Calendar for a unified schedule view',
                'Generate financial forecasts and optimization suggestions',
                'Improve our AI task extraction accuracy over time',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-[#596065]">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#005bc1] mt-2 flex-shrink-0"></div>
                  {item}
                </li>
              ))}
            </ul>
          </section>

          {/* Data We Do NOT Collect */}
          <section className="bg-white rounded-2xl p-8 border border-[#ebeef2]">
            <h2 className="text-xl font-bold text-[#2d3338] mb-4">What We Do NOT Do</h2>
            <ul className="space-y-3">
              {[
                'We do NOT sell, rent, or share your personal data with third parties',
                'We do NOT store full email content — only metadata needed for task extraction',
                'We do NOT use your data for advertising or marketing purposes',
                'We do NOT train AI models on your personal data outside your private instance',
                'We do NOT access your email attachments, contacts, or sent messages',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-[#596065]">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#ba1a1a] mt-2 flex-shrink-0"></div>
                  {item}
                </li>
              ))}
            </ul>
          </section>

          {/* Third Party Services */}
          <section className="bg-white rounded-2xl p-8 border border-[#ebeef2]">
            <h2 className="text-xl font-bold text-[#2d3338] mb-4">Third-Party Services</h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-[#2d3338] mb-1">Google APIs</h3>
                <p className="text-sm text-[#596065] leading-relaxed">
                  We use Google OAuth 2.0 for authentication and Gmail/Calendar API access. Our use of Google APIs complies 
                  with <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer" className="text-[#005bc1] underline">Google API Services User Data Policy</a>, 
                  including the Limited Use requirements.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-[#2d3338] mb-1">AI Processing</h3>
                <p className="text-sm text-[#596065] leading-relaxed">
                  We use AI language models to extract task information from email snippets. Only minimal email metadata 
                  (subject line and preview text) is sent for processing. No personally identifiable information is retained 
                  by the AI service after processing.
                </p>
              </div>
            </div>
          </section>

          {/* Data Security */}
          <section className="bg-white rounded-2xl p-8 border border-[#ebeef2]">
            <h2 className="text-xl font-bold text-[#2d3338] mb-4">Data Security</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { title: 'Encryption', desc: 'All data is encrypted in transit (TLS/HTTPS) and at rest' },
                { title: 'Secure Authentication', desc: 'Passwords are hashed with bcrypt. OAuth tokens are stored securely' },
                { title: 'Minimal Data Retention', desc: 'We only store what is necessary to provide the service' },
                { title: 'Access Controls', desc: 'Your data is isolated and only accessible with your authentication' },
              ].map((item, i) => (
                <div key={i} className="p-4 bg-[#f0fdf4] border border-[#bbf7d0] rounded-xl">
                  <h4 className="font-semibold text-sm text-[#0f6b3a] mb-1">{item.title}</h4>
                  <p className="text-xs text-[#596065]">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Your Rights */}
          <section className="bg-white rounded-2xl p-8 border border-[#ebeef2]">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#fef3c7] flex items-center justify-center">
                <Trash size={22} className="text-[#b45309]" weight="fill" />
              </div>
              <h2 className="text-xl font-bold text-[#2d3338]">Your Rights</h2>
            </div>
            <ul className="space-y-3">
              {[
                { right: 'Access', desc: 'You can view all data we have about you through the app' },
                { right: 'Disconnect', desc: 'You can disconnect Gmail and Calendar at any time from the Connections page. This immediately revokes our access' },
                { right: 'Delete', desc: 'You can delete individual tasks or request full account deletion by contacting us' },
                { right: 'Export', desc: 'You can export your task data at any time' },
                { right: 'Revoke', desc: 'You can revoke Google permissions at any time via your Google Account settings' },
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-[#596065]">
                  <span className="text-xs font-bold text-[#005bc1] bg-[#d6e3ff] px-2 py-0.5 rounded mt-0.5 flex-shrink-0">{item.right}</span>
                  {item.desc}
                </li>
              ))}
            </ul>
          </section>

          {/* Contact */}
          <section className="bg-white rounded-2xl p-8 border border-[#ebeef2]">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#f2f4f6] flex items-center justify-center">
                <EnvelopeSimple size={22} className="text-[#596065]" weight="fill" />
              </div>
              <h2 className="text-xl font-bold text-[#2d3338]">Contact Us</h2>
            </div>
            <p className="text-sm text-[#596065] leading-relaxed">
              If you have questions about this privacy policy or your data, please contact us at{' '}
              <a href="mailto:privacy@lifeadmin.ai" className="text-[#005bc1] underline">privacy@lifeadmin.ai</a>.
            </p>
          </section>

          {/* Google Compliance */}
          <section className="bg-[#f0f7ff] rounded-2xl p-8 border border-[#bfdbfe]">
            <h2 className="text-lg font-bold text-[#1d4ed8] mb-3">Google API Services Compliance</h2>
            <p className="text-sm text-[#475569] leading-relaxed">
              LifeAdmin AI's use and transfer to any other app of information received from Google APIs will adhere to{' '}
              <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer" className="text-[#005bc1] underline font-medium">
                Google API Services User Data Policy
              </a>, including the Limited Use requirements.
            </p>
          </section>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-[#ebeef2] flex items-center justify-between">
          <p className="text-sm text-[#acb3b8]">&copy; 2026 LifeAdmin AI. All rights reserved.</p>
          <Link to="/" className="text-sm text-[#005bc1] hover:underline">Back to Home</Link>
        </div>
      </div>
    </div>
  );
}
